import {Context, createContext, useCallback, useContext, useMemo} from "react";
import {SlidingPanel} from "./SlidingPanel";

/**
 * Hook executed right before a panel navigates "back".
 * Return (or resolve to) `false` to cancel the back navigation; any other value
 * (including `undefined`) lets the navigation proceed.
 */
export type BackHandler = () => void | boolean | Promise<void | boolean>;

export interface SlidingPanelsContextType {
	next: (panel: SlidingPanel<any>) => Promise<void>;
	back: () => Promise<void>;
	/**
	 * Registers (or clears, when passed `null`) the back handler for the panel at
	 * the given index. Prefer {@link UseSlidingPanelsResult.setBackHandler}, which
	 * binds the handler to the calling panel automatically.
	 */
	registerBackHandler: (index: number, handler: BackHandler | null) => void;
}

export interface UseSlidingPanelsResult extends SlidingPanelsContextType {
	/**
	 * Registers a hook that runs before this panel navigates back. Passing `null`
	 * clears it. The hook may be async and may return `false` to cancel the back.
	 */
	setBackHandler: (handler: BackHandler | null) => void;
}

export const SlidingPanelsContext: Context<SlidingPanelsContextType | null> = createContext<SlidingPanelsContextType | null>(null);

/** Provides each rendered panel with its own index in the stack. */
export const PanelIndexContext: Context<number> = createContext<number>(-1);

export function useSlidingPanels(): UseSlidingPanelsResult {
	const context = useContext(SlidingPanelsContext);
	const panelIndex = useContext(PanelIndexContext);
	if (!context) {
		throw new Error("useSlidingPanels must be used within SlidingPanelProvider");
	}

	const {registerBackHandler} = context;
	const setBackHandler = useCallback(
		(handler: BackHandler | null) => registerBackHandler(panelIndex, handler),
		[registerBackHandler, panelIndex]
	);

	return useMemo(() => ({...context, setBackHandler}), [context, setBackHandler]);
}
